/**
 * MasterDataDeliveryTermFormDialog — Create/Edit `delivery_term`.
 * Compact Sales Contract style (mirrors MasterDataDocDispatchFormDialog).
 *
 * Single-column, field order by Top:
 *   Delivery Term → Description → Description on Contract.
 * `delivery_term` là PK text: required, disable khi edit.
 */
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useEffect, useState } from 'react'

import { useSnackbar } from '@/hooks/useSnackbar'
import {
  useCreateMdDeliveryTermMutation,
  useUpdateMdDeliveryTermMutation,
} from '@/store/api/masterDataCrudApi'
import type { MasterDataDeliveryTerm } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import { compactDialogContentSx, dialogHeaderBtnSx, dialogTitleActionsSx, dialogTitleSx, dialogTitleTextSx, roSx } from '../purchase/dialogStyle'

export interface Props {
  open: boolean
  mode: 'create' | 'edit'
  initialValue?: MasterDataDeliveryTerm | null
  onClose: () => void
  onSaved?: () => void
}

type FormState = {
  delivery_term: string
  description: string
  description_on_contract: string
}

const EMPTY: FormState = {
  delivery_term: '',
  description: '',
  description_on_contract: '',
}

export default function MasterDataDeliveryTermFormDialog({
  open,
  mode,
  initialValue,
  onClose,
  onSaved,
}: Props) {
  const snack = useSnackbar()
  const isEdit = mode === 'edit'

  const [createDeliveryTerm, { isLoading: creating }] = useCreateMdDeliveryTermMutation()
  const [updateDeliveryTerm, { isLoading: updating }] = useUpdateMdDeliveryTermMutation()
  const loading = creating || updating

  const [form, setForm] = useState<FormState>(EMPTY)
  const [showErrors, setShowErrors] = useState(false)

  useEffect(() => {
    if (!open) return
    setShowErrors(false)
    if (isEdit && initialValue) {
      setForm({
        delivery_term: initialValue.delivery_term ?? '',
        description: initialValue.description ?? '',
        description_on_contract: initialValue.description_on_contract ?? '',
      })
    } else {
      setForm(EMPTY)
    }
  }, [open, isEdit, initialValue])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((p) => ({ ...p, [k]: v }))

  const codeEmpty = !form.delivery_term.trim()
  const descEmpty = !form.description.trim()
  const descContractEmpty = !form.description_on_contract.trim()
  const canSave = !codeEmpty && !descEmpty && !descContractEmpty && !loading

  const handleClose = () => {
    if (loading) return
    onClose()
  }

  const handleSave = async () => {
    // Access frmMdQryDeliveryTermAdd.btnSave_Click: 3 field đều required, báo tuần tự.
    if (codeEmpty || descEmpty || descContractEmpty) {
      setShowErrors(true)
      if (codeEmpty) snack.warning('Please type Delivery Term!')
      else if (descEmpty) snack.warning('Please type Description!')
      else snack.warning('Please type Delivery On Contract!')
      return
    }
    try {
      if (isEdit && initialValue) {
        await updateDeliveryTerm({
          code: initialValue.delivery_term,
          body: {
            description: form.description.trim(),
            description_on_contract: form.description_on_contract.trim(),
          },
        }).unwrap()
        snack.success('Delivery term updated')
      } else {
        await createDeliveryTerm({
          delivery_term: form.delivery_term.trim(),
          description: form.description.trim(),
          description_on_contract: form.description_on_contract.trim(),
        }).unwrap()
        snack.success('Delivery term created')
      }
      onSaved?.()
      onClose()
    } catch (e) {
      snack.error(getErrorMessage(e, isEdit ? 'Failed to update' : 'Failed to create'))
    }
  }

  return (
    <Dialog
      open={open}
      onClose={(_event, reason) => {
        if (loading) return
        if (reason === 'backdropClick') return
        handleClose()
      }}
      maxWidth="sm"
      fullWidth
      scroll="paper"
    >
      <DialogTitle sx={dialogTitleSx}>
        <Typography component="span" noWrap sx={dialogTitleTextSx}>
          {isEdit ? 'Edit Delivery Term' : 'New Delivery Term'}
          {isEdit && initialValue ? (
            <Typography component="span" variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
              {initialValue.delivery_term}
            </Typography>
          ) : null}
        </Typography>
        <Stack direction="row" spacing={0.5} sx={dialogTitleActionsSx}>
          <Button
            type="button"
            variant="contained"
            size="small"
            color="success"
            onClick={() => void handleSave()}
            disabled={!canSave}
            sx={dialogHeaderBtnSx}
          >
            {loading ? <CircularProgress size={14} color="inherit" /> : 'Save'}
          </Button>
          <Button type="button" variant="outlined" size="small" onClick={handleClose} disabled={loading} sx={dialogHeaderBtnSx}>
            Cancel
          </Button>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent dividers sx={compactDialogContentSx}>
        <Grid container spacing={1.5} sx={{ mt: 0 }}>
          <Grid item xs={12}>
            <TextField
              label="Delivery Term"
              size="small"
              fullWidth
              required
              disabled={isEdit}
              error={showErrors && codeEmpty}
              value={form.delivery_term}
              onChange={(e) => set('delivery_term', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: isEdit ? -1 : 1 }}
              helperText={isEdit ? 'Delivery Term cannot be changed after creation.' : undefined}
              sx={isEdit ? roSx : undefined}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description"
              size="small"
              fullWidth
              required
              multiline
              minRows={2}
              error={showErrors && descEmpty}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 2 }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Description on Contract"
              size="small"
              fullWidth
              required
              multiline
              minRows={2}
              error={showErrors && descContractEmpty}
              value={form.description_on_contract}
              onChange={(e) => set('description_on_contract', e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ tabIndex: 3 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  )
}
